import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement,
  Title, Tooltip, Legend,
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import { funnelAPI } from '../../services/api';
import { Spinner } from '../../components/ui';
import toast from 'react-hot-toast';
import { useRole } from '../../hooks/useRole';
import AccessDenied from '../../components/AccessDenied';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const NAVY = '#1B2A6B';
const GOLD = '#C9A84C';

const UTM_CHIPS = [
  { label: 'utm_source=google', color: '#4285F4' },
  { label: 'utm_medium=cpc', color: '#34A853' },
  { label: 'utm_campaign=franchise-india', color: '#EA4335' },
  { label: 'utm_source=facebook', color: '#1877F2' },
  { label: 'utm_medium=paid_social', color: '#E91E8C' },
  { label: 'utm_campaign=student-apply', color: '#FF6D00' },
];

function CopyChip({ label, color }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(label); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      style={{
        padding: '5px 12px', borderRadius: 6, border: `1.5px solid ${color}`,
        background: copied ? color : '#fff', color: copied ? '#fff' : color,
        fontSize: 12, fontFamily: 'monospace', cursor: 'pointer', fontWeight: 600, transition: 'all 0.15s',
      }}>
      {copied ? '✓ Copied' : label}
    </button>
  );
}

// Inline horizontal funnel step
function FunnelStep({ label, count, maxCount, isLast }) {
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: isLast ? 0 : 8 }}>
      <div style={{ width: 130, fontSize: 12, fontWeight: 600, color: '#475569', textAlign: 'right', flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, height: 28, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, ${NAVY}, #2d3f8a)`,
          borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          paddingRight: 8, transition: 'width 0.6s ease', minWidth: count > 0 ? 40 : 0,
        }}>
          {count > 0 && <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>{count}</span>}
        </div>
      </div>
      <div style={{ width: 50, textAlign: 'right', fontSize: 12, color: '#94a3b8' }}>
        {pct.toFixed(0)}%
      </div>
    </div>
  );
}

export default function FunnelAnalyticsPage() {
  const { isAdminOrAbove } = useRole();
  const canAccess = isAdminOrAbove;
  const [overview, setOverview] = useState(null);
  const [convData, setConvData] = useState(null);
  const [sourceData, setSourceData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canAccess) return;
    Promise.all([
      funnelAPI.getOverview(),
      funnelAPI.getConversion(),
      funnelAPI.getBySource(),
    ]).then(([ov, cv, sv]) => {
      setOverview(ov.data?.data);
      setConvData(cv.data?.data);
      setSourceData(sv.data?.data);
    }).catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [canAccess]);

  if (!canAccess) return <AccessDenied />;

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner /></div>
  );

  const funnelSteps = convData?.funnel || [];
  const maxFunnelCount = funnelSteps[0]?.count || 1;

  // Leads by source bar chart data
  const leadSources = sourceData?.by_lead_source || [];
  const barData = {
    labels: leadSources.map(s => s._id || 'unknown'),
    datasets: [{
      label: 'Leads',
      data: leadSources.map(s => s.count),
      backgroundColor: [NAVY, GOLD, '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#f59e0b'],
      borderRadius: 6,
    }],
  };
  const barOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ` ${ctx.raw} leads` } } },
    scales: { y: { beginAtZero: true, grid: { color: '#f1f5f9' } }, x: { grid: { display: false } } },
  };

  // By page type pie chart
  const byType = convData?.by_page_type || [];
  const TYPE_COLORS = { franchise: NAVY, student: GOLD, institutional: '#10b981', null: '#94a3b8' };
  const pieData = {
    labels: byType.map(t => t._id || 'Unknown'),
    datasets: [{
      data: byType.map(t => t.count),
      backgroundColor: byType.map(t => TYPE_COLORS[t._id] || '#94a3b8'),
      borderWidth: 2, borderColor: '#fff',
    }],
  };
  const pieOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom', labels: { padding: 14, font: { size: 12 } } } },
  };

  const utmCampaigns = convData?.utm_campaigns || [];
  const pages = overview?.pages || [];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: NAVY, margin: 0 }}>Lead Generation Analytics</h1>
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>Track your funnel performance and campaign ROI</p>
      </div>

      {/* Top Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Total Page Views',     value: overview?.total_views ?? 0,       color: NAVY,     icon: '👁️' },
          { label: 'Form Submissions',     value: overview?.total_submissions ?? 0,  color: '#7c3aed', icon: '📋' },
          { label: 'Leads Created',        value: overview?.total_leads ?? 0,        color: '#15803d', icon: '🎯' },
          { label: 'Conversion Rate',      value: `${overview?.conversion_rate ?? 0}%`, color: GOLD,  icon: '📈' },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '16px 18px' }}>
            <div style={{ fontSize: 22, marginBottom: 6 }}>{s.icon}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* Bar: Leads by Source */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '20px 20px' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: NAVY, margin: '0 0 16px' }}>Leads by Source</h3>
          {leadSources.length === 0 ? (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13 }}>
              No lead data yet
            </div>
          ) : (
            <div style={{ height: 220 }}>
              <Bar data={barData} options={barOptions} />
            </div>
          )}
        </div>

        {/* Pie: by page type */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '20px 20px' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: NAVY, margin: '0 0 16px' }}>Submissions by Page Type</h3>
          {byType.length === 0 ? (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13 }}>
              No submission data yet
            </div>
          ) : (
            <div style={{ height: 220 }}>
              <Pie data={pieData} options={pieOptions} />
            </div>
          )}
        </div>
      </div>

      {/* Horizontal Funnel */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '20px 22px', marginBottom: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: NAVY, margin: '0 0 16px' }}>Funnel Conversion</h3>
        {funnelSteps.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 20 }}>No funnel data yet</div>
        ) : (
          funnelSteps.map((step, i) => (
            <FunnelStep
              key={step.label}
              label={step.label}
              count={step.count}
              maxCount={maxFunnelCount}
              isLast={i === funnelSteps.length - 1}
            />
          ))
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* Top Funnel Pages Table */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '16px 18px', borderBottom: '1px solid #f1f5f9' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: NAVY, margin: 0 }}>Funnel Pages Performance</h3>
          </div>
          {pages.length === 0 ? (
            <div style={{ padding: '24px', color: '#94a3b8', fontSize: 13, textAlign: 'center' }}>No pages yet</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Page', 'Visits', 'Submissions', 'Rate'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', borderBottom: '1px solid #f1f5f9' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pages.map(p => {
                  const rate = p.total_visits > 0 ? ((p.total_submissions / p.total_visits) * 100).toFixed(1) : 0;
                  return (
                    <tr key={p._id} style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{p.title}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>/{p.slug}</div>
                      </td>
                      <td style={{ padding: '10px 14px', fontSize: 13, color: '#475569', fontWeight: 600 }}>{p.total_visits}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13, color: '#475569', fontWeight: 600 }}>{p.total_submissions}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{
                          fontSize: 12, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
                          background: rate >= 10 ? '#dcfce7' : rate >= 3 ? '#fef9c3' : '#fee2e2',
                          color: rate >= 10 ? '#15803d' : rate >= 3 ? '#a16207' : '#dc2626',
                        }}>{rate}%</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* UTM Campaigns Table */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '16px 18px', borderBottom: '1px solid #f1f5f9' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: NAVY, margin: 0 }}>UTM Campaign Performance</h3>
          </div>
          {utmCampaigns.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              Launch your first campaign to see data here
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Source', 'Medium', 'Campaign', 'Leads'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', borderBottom: '1px solid #f1f5f9' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {utmCampaigns.map((u, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f8fafc' }}>
                    <td style={{ padding: '9px 12px', fontSize: 12, color: '#475569' }}>{u.source}</td>
                    <td style={{ padding: '9px 12px', fontSize: 12, color: '#475569' }}>{u.medium}</td>
                    <td style={{ padding: '9px 12px', fontSize: 12, color: '#1e293b', fontWeight: 600, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.campaign}</td>
                    <td style={{ padding: '9px 12px', fontSize: 13, fontWeight: 800, color: NAVY }}>{u.leads}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Ad Readiness Card */}
      <div style={{ background: '#fff', border: `2px solid ${GOLD}`, borderRadius: 14, padding: '20px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span style={{ fontSize: 24 }}>📡</span>
          <div>
            <h3 style={{ fontSize: 14, fontWeight: 800, color: NAVY, margin: 0 }}>Ad Readiness — UTM Setup</h3>
            <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>
              When you launch Google or Meta ads, use these UTM parameters to auto-track ROI in this dashboard:
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {UTM_CHIPS.map(chip => (
            <CopyChip key={chip.label} label={chip.label} color={chip.color} />
          ))}
        </div>
        <p style={{ fontSize: 11, color: '#94a3b8', margin: '10px 0 0' }}>
          Click any chip to copy · Append these to your landing page URLs to track campaign performance automatically
        </p>
      </div>
    </div>
  );
}
